import React, { useState, useRef, useEffect, forwardRef } from "react";
import DatePicker from "react-datepicker";
import { FaCalendar, FaClock } from "react-icons/fa";
import { format } from "date-fns";
import "react-datepicker/dist/react-datepicker.css";

// Custom input component that matches your existing design
const CustomInput = forwardRef(
  (
    {
      value,
      onClick,
      placeholder,
      label,
      required,
      error,
      showTime,
      disabled,
      className = "",
    },
    ref
  ) => (
    <div className="w-full">
      {label && (
        <label className="block text-base font-medium text-[#000000] mb-2">
          {label} {required && <span className="text-red-500">*</span>}
        </label>
      )}
      <div className="relative">
        <input
          ref={ref}
          value={value}
          onClick={onClick}
          placeholder={placeholder}
          readOnly
          disabled={disabled}
          className={`w-full px-4 py-3 ${
            className.includes('border-[') ? '' : `border ${error ? "border-red-500" : "border-[#E9E9E9]"}`
          } rounded-lg text-base font-medium placeholder-[#00000099] focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent cursor-pointer ${
            disabled ? "opacity-50 cursor-not-allowed" : ""
          } ${className}`}
        />
        <div className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none">
          {showTime ? (
            <FaClock className="text-gray-400 text-sm" />
          ) : (
            <FaCalendar className="text-gray-400 text-sm" />
          )}
        </div>
      </div>
      {error && <p className="text-sm text-red-600 mt-1">{error}</p>}
    </div>
  )
);

CustomInput.displayName = "CustomInput";

// Modern Custom Dropdown Component
const ModernDropdown = ({
  options,
  value,
  onChange,
  placeholder = "Select...",
  className = "",
  maxHeight = "200px",
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  const listRef = useRef(null);
  const selectedOptionRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  // Scroll to selected option when dropdown opens
  useEffect(() => {
    if (isOpen && selectedOptionRef.current && listRef.current) {
      const listElement = listRef.current;
      const selectedElement = selectedOptionRef.current;

      // Calculate scroll position to center the selected item
      const listHeight = listElement.clientHeight;
      const selectedTop = selectedElement.offsetTop;
      const selectedHeight = selectedElement.clientHeight;

      // Scroll to center the selected item in the list
      listElement.scrollTop = selectedTop - (listHeight / 2) + (selectedHeight / 2);
    }
  }, [isOpen]);

  const selectedOption = options.find((option) => option.value === value);

  return (
    <div ref={dropdownRef} className={`relative ${className}`}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full bg-white border border-gray-200 rounded-lg px-3 py-2 text-left text-sm font-medium text-gray-900 cursor-pointer hover:border-blue-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 shadow-sm hover:shadow-md flex items-center justify-between min-w-[80px]"
      >
        <span className="truncate">
          {selectedOption ? selectedOption.label : placeholder}
        </span>
        <svg
          className={`w-4 h-4 text-gray-500 transition-transform duration-200 ${
            isOpen ? "rotate-180" : ""
          }`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 9l-7 7-7-7"
          />
        </svg>
      </button>

      {isOpen && (
        <div className="absolute top-full left-0 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg z-50 overflow-hidden">
          <div
            ref={listRef}
            className="overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100"
            style={{ maxHeight }}
          >
            {options.map((option) => (
              <button
                key={option.value}
                ref={option.value === value ? selectedOptionRef : null}
                type="button"
                onClick={() => {
                  onChange(option.value);
                  setIsOpen(false);
                }}
                className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-50 transition-colors duration-150 ${
                  option.value === value
                    ? "bg-blue-50 text-blue-700 font-medium"
                    : "text-gray-900"
                }`}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// Custom header component for the date picker with modern dropdowns
const CustomHeader = ({
  date,
  changeYear,
  changeMonth,
  decreaseMonth,
  increaseMonth,
  prevMonthButtonDisabled,
  nextMonthButtonDisabled,
}) => {
  const months = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  // Generate year options (from 1900 to current year + 10)
  const currentYear = new Date().getFullYear();
  const years = [];
  for (let year = 1900; year <= currentYear + 10; year++) {
    years.push(year);
  }

  // Convert arrays to dropdown options
  const monthOptions = months.map((month, index) => ({
    value: index,
    label: month,
  }));

  const yearOptions = years.map((year) => ({
    value: year,
    label: year.toString(),
  }));

  return (
    <div className="flex items-center justify-between px-2 py-2 bg-gray-50 rounded-t-lg">
      <button
        onClick={decreaseMonth}
        disabled={prevMonthButtonDisabled}
        className="p-1.5 hover:bg-white hover:shadow-sm rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
      >
        <svg
          className="w-3.5 h-3.5 text-gray-600"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M15 19l-7-7 7-7"
          />
        </svg>
      </button>

      <div className="flex items-center gap-2">
        {/* Month Dropdown */}
        <ModernDropdown
          options={monthOptions}
          value={date.getMonth()}
          onChange={changeMonth}
          className="min-w-[100px]"
        />

        {/* Year Dropdown */}
        <ModernDropdown
          options={yearOptions}
          value={date.getFullYear()}
          onChange={changeYear}
          className="min-w-[70px]"
          maxHeight="140px"
        />
      </div>

      <button
        onClick={increaseMonth}
        disabled={nextMonthButtonDisabled}
        className="p-1.5 hover:bg-white hover:shadow-sm rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
      >
        <svg
          className="w-3.5 h-3.5 text-gray-600"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M9 5l7 7-7 7"
          />
        </svg>
      </button>
    </div>
  );
};

// Time picker component with auto-scroll to selected time
const TimePickerWithAutoScroll = ({ value, handleChange, timeIntervals, props }) => {
  const timeContainerRef = useRef(null);

  useEffect(() => {
    // Wait for the component to render, then scroll to selected time
    const scrollToSelectedTime = () => {
      if (timeContainerRef.current) {
        const selectedTimeItem = timeContainerRef.current.querySelector(
          '.react-datepicker__time-list-item--selected'
        );
        if (selectedTimeItem) {
          const listContainer = timeContainerRef.current.querySelector(
            '.react-datepicker__time-list'
          );
          if (listContainer) {
            const listHeight = listContainer.clientHeight;
            const itemTop = selectedTimeItem.offsetTop;
            const itemHeight = selectedTimeItem.clientHeight;
            listContainer.scrollTop = itemTop - (listHeight / 2) + (itemHeight / 2);
          }
        }
      }
    };

    // Small delay to ensure DOM is ready
    const timer = setTimeout(scrollToSelectedTime, 50);
    return () => clearTimeout(timer);
  }, [value]);

  return (
    <div className="flex flex-col" ref={timeContainerRef}>
      <div className="text-sm font-semibold text-gray-900 mb-2">
        Time
      </div>
      <div className="bg-white border border-gray-200 rounded-lg p-2 min-w-[120px]">
        <DatePicker
          selected={
            value
              ? typeof value === "string"
                ? new Date(value)
                : value
              : null
          }
          onChange={handleChange}
          showTimeSelect={true}
          showTimeSelectOnly={true}
          timeIntervals={timeIntervals}
          inline
          timeFormat="HH:mm"
          timeCaption=""
          calendarClassName="custom-timepicker"
          {...props}
        />
      </div>
    </div>
  );
};

const CustomDateTimePicker = ({
  value,
  onChange,
  label,
  placeholder,
  required = false,
  error,
  disabled = false,
  showTimeSelect = false,
  showTimeSelectOnly = false,
  timeIntervals = 15,
  minDate,
  maxDate,
  dateFormat,
  className = "",
  closeOnClickOutside = true,
  openToLeft = false, // New flag to control position
  openToTop = false,
  position = "right-0",
  ...props
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const datePickerRef = useRef(null);
  const containerRef = useRef(null);

  // Handle click outside to close the picker
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!closeOnClickOutside) return;

      if (
        containerRef.current &&
        !containerRef.current.contains(event.target)
      ) {
        // Check if the click is on a scrollbar or within a modal
        const target = event.target;
        const isScrollbar =
          target.classList.contains("scrollbar") ||
          target.classList.contains("scrollbar-thumb") ||
          target.classList.contains("scrollbar-track");

        // Check if click is within a modal container
        const modalContainer = target.closest('[role="dialog"]');
        const isWithinModal =
          modalContainer && modalContainer !== containerRef.current;

        if (!isScrollbar && !isWithinModal) {
          setIsOpen(false);
        }
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      document.addEventListener("keydown", (e) => {
        if (e.key === "Escape") {
          setIsOpen(false);
        }
      });
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen, closeOnClickOutside]);

  // Determine the format based on showTimeSelect
  const getDateFormat = () => {
    if (dateFormat) return dateFormat;
    if (showTimeSelectOnly) return "HH:mm";
    if (showTimeSelect) return "dd/MM/yyyy HH:mm";
    return "dd/MM/yyyy";
  };

  // Format the display value
  const getDisplayValue = () => {
    if (!value) return "";
    try {
      const date = typeof value === "string" ? new Date(value) : value;
      if (isNaN(date.getTime())) return "";
      return format(date, getDateFormat());
    } catch (error) {
      return "";
    }
  };

  // Convert date to local ISO string without timezone conversion
  const toLocalISOString = (date) => {
    if (!date) return "";

    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    const hours = String(date.getHours()).padStart(2, "0");
    const minutes = String(date.getMinutes()).padStart(2, "0");

    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  const handleChange = (date) => {
    if (onChange) {
      // Pass the date object directly, let the parent handle the conversion
      onChange(date);
    }
    setIsOpen(false);
  };

  const handleInputClick = () => {
    if (!disabled) {
      setIsOpen(!isOpen);
    }
  };

  return (
    <div ref={containerRef} className="relative">
      <CustomInput
        ref={datePickerRef}
        value={getDisplayValue()}
        onClick={handleInputClick}
        placeholder={
          placeholder || `Select ${showTimeSelectOnly ? "time" : "date"}`
        }
        label={label}
        required={required}
        error={error}
        showTime={showTimeSelect || showTimeSelectOnly}
        disabled={disabled}
        className={className}
      />

      {isOpen && (
        <div
          className={`absolute top-full mt-1 z-50 bg-white rounded-lg shadow-xl border border-gray-200 p-2 min-w-[320px] ${position}`}
        >
          <div className="flex justify-between items-center mb-2">
            <h3 className="text-sm font-semibold text-gray-900">
              Select{" "}
              {showTimeSelectOnly
                ? "Time"
                : showTimeSelect
                ? "Date & Time"
                : "Date"}
            </h3>
            <button
              onClick={() => setIsOpen(false)}
              className="text-gray-500 hover:text-gray-700 text-lg font-bold p-0.5 hover:bg-gray-100 rounded"
            >
              ×
            </button>
          </div>

          <div className="flex gap-4">
            <DatePicker
              selected={
                value
                  ? typeof value === "string"
                    ? new Date(value)
                    : value
                  : null
              }
              onChange={handleChange}
              showTimeSelect={false}
              showTimeSelectOnly={false}
              timeIntervals={timeIntervals}
              minDate={minDate}
              maxDate={maxDate}
              inline
              renderCustomHeader={CustomHeader}
              calendarClassName="custom-datepicker"
              showMonthDropdown
              showYearDropdown
              dropdownMode="select"
              {...props}
            />

            {(showTimeSelect || showTimeSelectOnly) && (
              <TimePickerWithAutoScroll
                value={value}
                handleChange={handleChange}
                timeIntervals={timeIntervals}
                props={props}
              />
            )}
          </div>

          <div className="flex justify-end gap-2 mt-2 pt-2 border-t border-gray-100">
            <button
              onClick={() => setIsOpen(false)}
              className="px-3 py-1 text-sm text-gray-600 bg-gray-100 rounded hover:bg-gray-200 transition-colors"
            >
              Cancel
            </button>
            {value && (
              <button
                onClick={() => handleChange(null)}
                className="px-3 py-1 text-sm text-red-600 bg-red-50 rounded hover:bg-red-100 transition-colors"
              >
                Clear
              </button>
            )}
          </div>
        </div>
      )}

      {/* Inject custom styles for this specific instance */}
      {isOpen && (
        <style>
          {`
            .custom-datepicker {
              border: none !important;
              box-shadow: none !important;
              font-family: inherit !important;
            }
            
            .custom-datepicker .react-datepicker__header {
              background-color: transparent !important;
              border-bottom: none !important;
              padding: 0 !important;
            }
            
            .custom-datepicker .react-datepicker__day--selected,
            .custom-datepicker .react-datepicker__day--in-selecting-range,
            .custom-datepicker .react-datepicker__day--in-range {
              background-color: #2283FF !important;
              color: white !important;
              border-radius: 6px !important;
              font-weight: 600 !important;
            }
            
            .custom-datepicker .react-datepicker__day:hover {
              background-color: #dbeafe !important;
              border-radius: 6px !important;
            }
            
            .custom-datepicker .react-datepicker__day {
              border-radius: 6px !important;
              margin: 0.5px !important;
              font-weight: 500 !important;
              width: 32px !important;
              height: 32px !important;
              line-height: 32px !important;
              font-size: 14px !important;
            }
            
            .custom-datepicker .react-datepicker__day--keyboard-selected {
              background-color: #dbeafe !important;
              color: #1e40af !important;
              border-radius: 6px !important;
            }
            
            .custom-datepicker .react-datepicker__day--today {
              background-color: #fef3c7 !important;
              color: #92400e !important;
              border-radius: 6px !important;
              font-weight: 600 !important;
            }
            
            .custom-datepicker .react-datepicker__time-container {
              border-left: 1px solid #e5e7eb !important;
            }
            
            .custom-datepicker .react-datepicker__time-container .react-datepicker__time .react-datepicker__time-box ul.react-datepicker__time-list li.react-datepicker__time-list-item--selected {
              background-color: #2283FF !important;
              color: white !important;
              border-radius: 6px !important;
              font-weight: 600 !important;
            }
            
            .custom-datepicker .react-datepicker__time-container .react-datepicker__time .react-datepicker__time-box ul.react-datepicker__time-list li.react-datepicker__time-list-item:hover {
              background-color: #dbeafe !important;
              border-radius: 6px !important;
            }
            
            .custom-datepicker .react-datepicker__time-container .react-datepicker__time .react-datepicker__time-box ul.react-datepicker__time-list li.react-datepicker__time-list-item {
              padding: 8px 12px !important;
              font-size: 14px !important;
              font-weight: 500 !important;
            }
            
            .custom-datepicker .react-datepicker__time-container .react-datepicker__time .react-datepicker__time-box {
              width: 100px !important;
            }
            
            .custom-datepicker .react-datepicker__time-container .react-datepicker__time .react-datepicker__time-box ul.react-datepicker__time-list {
              border: 1px solid #e5e7eb !important;
              border-radius: 6px !important;
              background: white !important;
            }
            
            .custom-datepicker .react-datepicker__time-container .react-datepicker__time .react-datepicker__time-box ul.react-datepicker__time-list li.react-datepicker__time-list-item--disabled {
              color: #9ca3af !important;
              background: #f9fafb !important;
            }
            
            .custom-datepicker .react-datepicker__time-container .react-datepicker__time .react-datepicker__time-box ul.react-datepicker__time-list li.react-datepicker__time-list-item--disabled:hover {
              background: #f9fafb !important;
              cursor: not-allowed !important;
            }
            
            .custom-datepicker .react-datepicker__month-container {
              background: white !important;
              border-radius: 8px !important;
              box-shadow: 0 2px 4px -1px rgba(0, 0, 0, 0.1) !important;
            }
            
            .custom-datepicker .react-datepicker__month {
              padding: 8px !important;
            }
            
            .custom-datepicker .react-datepicker__day-name {
              color: #6b7280 !important;
              font-weight: 600 !important;
              font-size: 12px !important;
              width: 32px !important;
              height: 32px !important;
              line-height: 32px !important;
            }
            
            .custom-datepicker select {
              background-image: none !important;
              border: 1px solid #d1d5db !important;
              border-radius: 6px !important;
              padding: 4px 8px !important;
              font-size: 14px !important;
              font-weight: 500 !important;
            }
            
            .custom-datepicker select option {
              padding: 8px 12px !important;
              background: white !important;
              color: #374151 !important;
            }
            
            .custom-datepicker select option:hover {
              background: #f3f4f6 !important;
            }
            
            /* Custom time picker styles */
            .custom-timepicker {
              border: none !important;
              box-shadow: none !important;
              font-family: inherit !important;
            }
            
            .custom-timepicker .react-datepicker__header {
              display: none !important;
            }
            
            .custom-timepicker .react-datepicker__month-container {
              display: none !important;
            }
            
            .custom-timepicker .react-datepicker__time-container {
              border: none !important;
              width: 100% !important;
            }
            
            .custom-timepicker .react-datepicker__time-container .react-datepicker__time {
              background: transparent !important;
              border: none !important;
            }
            
            .custom-timepicker .react-datepicker__time-container .react-datepicker__time .react-datepicker__time-box {
              width: 100% !important;
            }
            
            .custom-timepicker .react-datepicker__time-container .react-datepicker__time .react-datepicker__time-box ul.react-datepicker__time-list {
              border: none !important;
              background: transparent !important;
              padding: 0 !important;
            }
            
            .custom-timepicker .react-datepicker__time-container .react-datepicker__time .react-datepicker__time-box ul.react-datepicker__time-list li.react-datepicker__time-list-item {
              padding: 6px 8px !important;
              font-size: 13px !important;
              font-weight: 500 !important;
              text-align: center !important;
              border-radius: 4px !important;
              margin: 1px 0 !important;
            }
            
            .custom-timepicker .react-datepicker__time-container .react-datepicker__time .react-datepicker__time-box ul.react-datepicker__time-list li.react-datepicker__time-list-item--selected {
              background-color: #2283FF !important;
              color: white !important;
              font-weight: 600 !important;
            }
            
            .custom-timepicker .react-datepicker__time-container .react-datepicker__time .react-datepicker__time-box ul.react-datepicker__time-list li.react-datepicker__time-list-item:hover {
              background-color: #dbeafe !important;
              color: #1e40af !important;
            }
            
            .custom-timepicker .react-datepicker__time-container .react-datepicker__time .react-datepicker__time-box ul.react-datepicker__time-list li.react-datepicker__time-list-item--disabled {
              color: #9ca3af !important;
              background: transparent !important;
            }
            
            .custom-timepicker .react-datepicker__time-container .react-datepicker__time .react-datepicker__time-box ul.react-datepicker__time-list li.react-datepicker__time-list-item--disabled:hover {
              background: transparent !important;
              cursor: not-allowed !important;
            }
            
            /* Custom scrollbar styles for modern dropdowns */
            .scrollbar-thin {
              scrollbar-width: thin;
              scrollbar-color: #d1d5db #f3f4f6;
            }
            
            .scrollbar-thin::-webkit-scrollbar {
              width: 6px;
            }
            
            .scrollbar-thin::-webkit-scrollbar-track {
              background: #f3f4f6;
              border-radius: 3px;
            }
            
            .scrollbar-thin::-webkit-scrollbar-thumb {
              background: #d1d5db;
              border-radius: 3px;
            }
            
            .scrollbar-thin::-webkit-scrollbar-thumb:hover {
              background: #9ca3af;
            }
            
            .scrollbar-thumb-gray-300::-webkit-scrollbar-thumb {
              background: #d1d5db;
            }
            
            .scrollbar-track-gray-100::-webkit-scrollbar-track {
              background: #f3f4f6;
            }
          `}
        </style>
      )}
    </div>
  );
};

export default CustomDateTimePicker;
